/**
 * Imperative toast event bus — zero dependencies.
 *
 * Usage:
 *   import { toast } from '@/components/ui/toast';
 *   toast.success('Patient saved');
 *   toast.error('Could not load analytics');
 *
 * The ToastProvider React component subscribes to this bus and renders
 * stacked toasts in the top-right corner.
 */

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  duration: number;
}

type Listener = (items: ToastItem[]) => void;

let items: ToastItem[] = [];
const listeners = new Set<Listener>();
let nextId = 1;

function notify(): void {
  for (const l of listeners) l(items);
}

function push(message: string, type: ToastType, duration = 4500): number {
  const id = nextId++;
  items = [...items, { id, message, type, duration }];
  notify();
  window.setTimeout(() => dismiss(id), duration);
  return id;
}

function dismiss(id: number): void {
  items = items.filter((t) => t.id !== id);
  notify();
}

/** Subscribe to toast state (used internally by ToastProvider). */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  listener(items);
  return () => { listeners.delete(listener); };
}

/** Current snapshot for late subscribers. */
export function getItems(): ToastItem[] { return items; }

export const toast = {
  success: (message: string, duration?: number) => push(message, 'success', duration),
  error:   (message: string, duration?: number) => push(message, 'error', duration ?? 7000),
  info:    (message: string, duration?: number) => push(message, 'info', duration),
  warning: (message: string, duration?: number) => push(message, 'warning', duration),
  dismiss,
};
